'use client';

import { Button } from '@core-components/Button';
import { createContext, useContext, useRef, useSyncExternalStore } from 'react';

import { DialogModal } from './DialogModal';
import { createDialogStore } from './dialogStore';
import { modalZoneStyles } from './styles';
import { Dialog } from './types';

// Dialog context

type IDialogContext = {
    store: ReturnType<typeof createDialogStore>;
};

const DialogContext = createContext<IDialogContext | null>(null);
DialogContext.displayName = 'DialogContext';

export { DialogContext };

// Provider functions

type DialogProviderProps = {
    children?: React.ReactNode;
};

export function DialogProvider({ children }: DialogProviderProps) {
    const dialogStore = useRef(createDialogStore());

    return (
        <DialogContext.Provider
            value={{
                store: dialogStore.current,
            }}
        >
            {children}
            <DialogZone />
        </DialogContext.Provider>
    );
}

export function DialogZone() {
    const { dialogs, close } = useDialogs();
    return (
        <section className={modalZoneStyles.blockScope}>
            {dialogs.map((dialog) => {
                const handleClose = () => close(dialog.id);
                return (
                    <DialogModal
                        key={dialog.id}
                        size={dialog.size}
                        open={true}
                        header={dialog.header}
                        footer={dialog.footer}
                        onClose={handleClose}
                    >
                        {dialog.content}
                    </DialogModal>
                );
            })}
        </section>
    );
}

export const useDialogs = () => {
    const context = useContext(DialogContext);

    if (!context) throw Error(`${DialogContext.displayName} is missing`);

    const { store } = context;

    type Dialogs = ReturnType<typeof store.getDialogs>;
    const dialogs: Dialogs = useSyncExternalStore(
        store.subscribe,
        store.getDialogs,
        store.getServerDialogs,
    );

    type OpenModalProps = Omit<Dialog, 'mode'>;

    const openModal = (props: OpenModalProps) => {
        const added = store.add({ mode: 'modal', ...props });
        return () => store.remove(added.id);
    };

    type ActionButtonProps =
        | string
        | {
              disabled?: boolean;
              label?: string;
              hide?: boolean;
          };

    type ConfirmModalProps = {
        size?: OpenModalProps['size'];
        header?: OpenModalProps['header'];
        content?: OpenModalProps['content'];
        actions?: {
            confirm?: ActionButtonProps;
            cancel?: ActionButtonProps;
        };
        onConfirm?: () => void;
        onCancel?: () => void;
    };

    const confirmModal = (props: ConfirmModalProps) => {
        const { actions } = props;
        const actionConfig = (props?: ActionButtonProps) => {
            if (!props) return;
            if (typeof props === 'string') return { label: props };
            return props;
        };
        const confirm = actionConfig(actions?.confirm);
        const cancel = actionConfig(actions?.cancel);
        const close = openModal({
            size: props.size || 'small',
            header: props.header,
            content: props.content,
            footer: {
                slot: (
                    <>
                        {confirm?.hide ? null : (
                            <Button
                                color="magenta"
                                onClick={() => {
                                    props?.onConfirm?.();
                                    close();
                                }}
                            >
                                {confirm?.label || 'Confirm'}
                            </Button>
                        )}
                        {cancel?.hide ? null : (
                            <Button
                                color="magenta"
                                variant="outlined"
                                onClick={() => {
                                    props?.onCancel?.();
                                    close();
                                }}
                            >
                                {cancel?.label || 'Cancel'}
                            </Button>
                        )}
                    </>
                ),
            },
        });
        return close;
    };

    const close = (id: number) => store.remove(id);

    return {
        dialogs,
        openModal,
        confirmModal,
        close,
    };
};
